import "./Account.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "../../component/PageHeader";
import Footer from "../../component/Footer";
import { FaRegEyeSlash, FaRegEye } from "react-icons/fa";
import { useAuth } from "../../component/context/AuthContext";

export default function Account() {
  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const idRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();

  const verifiedBanner = useMemo(() => {
    return !!location.state?.verified;
  }, [location.state]);

  const prefillEmail = useMemo(() => {
    const e = location.state?.email;
    return typeof e === "string" ? e : "";
  }, [location.state]);

  useEffect(() => {
    if (prefillEmail) setId(prefillEmail);
  }, [prefillEmail]);

  useEffect(() => {
    idRef.current?.focus();
  }, []);

  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const isValid = useMemo(() => {
    return id.trim().length >= 3 && pw.length >= 4;
  }, [id, pw]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!isValid) {
      setError("Please check your ID/PW.");
      return;
    }

    setSubmitting(true);

    try {
      await login(id, pw);
      navigate("/", { replace: true });
    } catch (err) {
      // If backend blocks login until verified, send user to check-email
      if (err?.code === "EMAIL_NOT_VERIFIED") {
        navigate("/check-email", {
          replace: true,
          state: { email: err.email || id },
        });
        return;
      }

      setError(err?.message || "Network Error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="Account">
      <PageHeader />
      <div className="account-content">
        <div className="account-card">
          <h1 className="account-title">Sign In</h1>

          {verifiedBanner && (
            <div className="account-success" role="status">
              Email verified! You can sign in now.
            </div>
          )}

          {error && (
            <div className="account-error" role="alert">
              {error}
            </div>
          )}

          <form className="account-form" onSubmit={handleSubmit} noValidate>
            <label className="account-label">
              Username / Email
              <input
                ref={idRef}
                type="text"
                autoComplete="username"
                value={id}
                onChange={(e) => setId(e.target.value)}
                className="account-input"
                placeholder="Username or Email"
                aria-invalid={!!error && !isValid}
              />
            </label>

            <label className="account-label">
              Password
              <div className="password-wrap">
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="account-input"
                  placeholder="Password"
                />
                <button
                  type="button"
                  className="toggle-pw"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide Password" : "View Password"}
                >
                  {showPw ? <FaRegEyeSlash /> : <FaRegEye />}
                </button>
              </div>
            </label>

            <button type="submit" className="account-btn" disabled={submitting || !isValid}>
              {submitting ? "Logging in…" : "Log In"}
            </button>
          </form>

          <div className="account-signup-findpw">
            <p className="account-help">
              Don’t have an account yet? <a href="/signup">Sign up</a>
            </p>
            <p className="account-help">
              <a href="/findpw">Forgot your password?</a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </section>
  );
}